import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../../../app/store';
import * as merchantRewardHistoryApi from './MerchantRewardHistory.api';
import {
    MerchantRewardHistoryFilter,
    mapApiResponseToUI,
    initialMerchantRewardHistoryPageState,
    MerchantRewardHistoryItemUI,
} from './MerchantRewardHistory.data';
import { showToast } from '../../../components/toast-store';
import type { BasePagingResponse } from '../../../lib/api-contract';
import { MerchantRewardHistoryItemApi, MerchantRewardHistoryStatisticApi } from './MerchantRewardHistory.data';

// Async Thunks
export interface FetchMerchantRewardHistoryRequest {
    filter: MerchantRewardHistoryFilter;
    pagination: { index: number; size: number };
}

export const fetchMerchantRewardHistory = createAsyncThunk<
    BasePagingResponse<MerchantRewardHistoryItemApi>,
    FetchMerchantRewardHistoryRequest,
    { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
    'merchantRewardHistory/fetchMerchantRewardHistory',
    async (request, { rejectWithValue }) => {
        try {
            const response = await merchantRewardHistoryApi.getMerchantRewardHistoryList({
                filter: request.filter,
                pagination: {
                    index: request.pagination.index - 1, // API uses 0-based indexing
                    size: request.pagination.size,
                },
            });

            if (response.success && response.data) {
                return response.data;
            }
            return rejectWithValue(response.user_message || 'Failed to load reward history');
        } catch (err: any) {
            return rejectWithValue(err.message || 'Failed to load reward history');
        }
    }
);

export const fetchMerchantRewardHistoryStatistic = createAsyncThunk<
    MerchantRewardHistoryStatisticApi,
    void,
    { rejectValue: string }
>(
    'merchantRewardHistory/fetchMerchantRewardHistoryStatistic',
    async (_, { rejectWithValue }) => {
        try {
            const response = await merchantRewardHistoryApi.getMerchantRewardHistoryStatistic();
            if (response.success && response.data) {
                return response.data;
            }
            return rejectWithValue(response.user_message || 'Failed to load statistics');
        } catch (err: any) {
            return rejectWithValue(err.message || 'Failed to load statistics');
        }
    }
);

export const resendMerchantReward = createAsyncThunk<
    { resend_count: number; resend_date: string },
    string,
    { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
    'merchantRewardHistory/resendMerchantReward',
    async (userCouponId, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await merchantRewardHistoryApi.resendMerchantReward(userCouponId);
            
            if (response.success && response.data) {
                // Refresh current page after resend
                const state = getState() as RootState;
                const merchantRewardHistoryState = state.merchantRewardHistory;
                dispatch(
                    fetchMerchantRewardHistory({
                        filter: merchantRewardHistoryState.filters,
                        pagination: {
                            index: merchantRewardHistoryState.pagination.index,
                            size: merchantRewardHistoryState.pagination.size,
                        },
                    })
                );
                
                return response.data;
            }
            return rejectWithValue(response.user_message || 'Failed to resend reward');
        } catch (err: any) {
            return rejectWithValue(err.message || 'Failed to resend reward');
        }
    }
);

// Slice
const merchantRewardHistorySlice = createSlice({
    name: 'merchantRewardHistory',
    initialState: initialMerchantRewardHistoryPageState,
    reducers: {
        setFilters(state, action: PayloadAction<MerchantRewardHistoryFilter>) {
            state.filters = { ...state.filters, ...action.payload };
        },
        updateFilter(state, action: PayloadAction<Partial<MerchantRewardHistoryFilter>>) {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters(state) {
            state.filters = {};
        },
        setPagination(state, action: PayloadAction<{ index: number; size?: number }>) {
            state.pagination = {
                ...state.pagination,
                index: action.payload.index,
                ...(action.payload.size && { size: action.payload.size }),
            };
        },
        setSelectedItem(state, action: PayloadAction<MerchantRewardHistoryItemUI | null>) {
            state.selectedItem = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch reward history list
            .addCase(fetchMerchantRewardHistory.pending, (state) => {
                state.loading.onGetList = true;
                state.error = null;
            })
            .addCase(fetchMerchantRewardHistory.fulfilled, (state, action) => {
                state.loading.onGetList = false;
                state.items = (action.payload.list || []).map(mapApiResponseToUI);
                state.pagination.count = action.payload.paging?.count || 0;
            })
            .addCase(fetchMerchantRewardHistory.rejected, (state, action) => {
                state.loading.onGetList = false;
                state.error = action.payload || 'Failed to load reward history';
                state.items = [];
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: action.payload || 'Failed to load reward history',
                });
            })
            // Fetch statistics
            .addCase(fetchMerchantRewardHistoryStatistic.pending, (state) => {
                state.loading.onGetStatistic = true;
            })
            .addCase(fetchMerchantRewardHistoryStatistic.fulfilled, (state, action) => {
                state.loading.onGetStatistic = false;
                state.statistic = {
                    totalRedeemedRewards: action.payload.total_redeemed_rewards || 0,
                    totalSentRewards: action.payload.total_sent_rewards || 0,
                };
            })
            .addCase(fetchMerchantRewardHistoryStatistic.rejected, (state, action) => {
                state.loading.onGetStatistic = false;
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: action.payload || 'Failed to load statistics',
                });
            })
            // Resend reward
            .addCase(resendMerchantReward.pending, (state) => {
                state.loading.onResend = true;
            })
            .addCase(resendMerchantReward.fulfilled, (state, action) => {
                state.loading.onResend = false;
                // Update the item if it's in the list
                const itemIndex = state.items.findIndex(item => item.id === state.selectedItem?.id);
                if (itemIndex !== -1 && state.selectedItem) {
                    state.items[itemIndex] = {
                        ...state.items[itemIndex],
                        actions: {
                            ...state.items[itemIndex].actions,
                            canResend: state.items[itemIndex].status === 'Viewed',
                            lastResent: new Date(action.payload.resend_date).toLocaleString(),
                        },
                    };
                }
                showToast({
                    type: 'success',
                    title: 'Success',
                    message: 'Reward resent successfully',
                });
            })
            .addCase(resendMerchantReward.rejected, (state, action) => {
                state.loading.onResend = false;
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: action.payload || 'Failed to resend reward',
                });
            });
    },
});

export const {
    setFilters,
    updateFilter,
    clearFilters,
    setPagination,
    setSelectedItem,
    clearError,
} = merchantRewardHistorySlice.actions;

export default merchantRewardHistorySlice.reducer;

